import * as React from 'react'

import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { UserLabel } from '@webapp/core/components/UserLabel'
import { useProcurifyIntl } from '@webapp/hooks'
import { CardholderSelectSelectedValueStyled } from '@webapp/modules/spending-cards/components/CardholderSelect/styles/CardholderSelectSelectedValueStyled'
import { type CardholderAccountStatusEnum } from '@webapp/modules/spending-cards/entities/enums/CardholderAccountStatusEnum'
import messages from './messages'
import { cardholderRepository } from '../../data-access/repositories/CardholderRepository'
import { Cardholder } from '../../entities/Cardholder'

const SelectedValue = (props: SingleValueProps<Cardholder, false>) => {
  const intl = useProcurifyIntl()
  const { data, selectProps } = props
  const { currentUserId } = selectProps as ICardholderSelectProps

  const displayName =
    currentUserId === data.userId
      ? `${data.fullName} ${intl.formatMessage(messages.selfCardholderLabel)}`
      : data.fullName

  return (
    <ReactSelectComponents.SingleValue {...props}>
      <CardholderSelectSelectedValueStyled>
        <UserLabel
          fluid={true}
          user={{ fullName: displayName, profileImage: data.profileImage }}
          WithUserTooltipProps={{
            disabled: true,
          }}
        />
      </CardholderSelectSelectedValueStyled>
    </ReactSelectComponents.SingleValue>
  )
}

const SelectOption = (props: OptionProps<Cardholder, false>) => {
  const intl = useProcurifyIntl()
  const { data, selectProps } = props
  const { currentUserId } = selectProps as ICardholderSelectProps

  const displayName =
    currentUserId === data.userId
      ? `${data.fullName} ${intl.formatMessage(messages.selfCardholderLabel)}`
      : data.fullName

  return (
    <ReactSelectComponents.Option {...props}>
      <UserLabel
        fluid={true}
        user={{ fullName: displayName, profileImage: data.profileImage }}
      />
    </ReactSelectComponents.Option>
  )
}

export interface ICardholderSelectProps extends IAsyncSelectProps<Cardholder> {
  cardholderStatus?: CardholderAccountStatusEnum
  currentUserId?: number
}

export const CardholderSelect = ({ ...props }: ICardholderSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps<Cardholder>['loadOptions'] = async (
    inputValue,
    pagination,
    callback
  ) => {
    const statusParam = props.cardholderStatus
      ? { status: props.cardholderStatus }
      : {}

    const res = await cardholderRepository.getCardholders({
      search: inputValue,
      page: pagination.page,
      ...statusParam,
    })

    callback({
      options: res.data.map((cardholder) => new Cardholder(cardholder)),
      pagination: {
        hasMore: res.pagination
          ? res.pagination?.page < res.pagination?.numPages
          : false,
        page: pagination.page + 1,
      },
    })
  }

  return (
    <AsyncSelect<Cardholder>
      cacheOptions={true}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      components={{
        SingleValue: SelectedValue,
        Option: SelectOption,
      }}
      getOptionLabel={(cardholder) => cardholder.fullName}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
